import { useEffect, useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Input, Button } from './ui';
import { LINE_BREAK, createTermRowItem, isBlankRow, parseTermLines, splitTermRow } from '../utils/termRows';
import type { TermRowItem } from '../utils/termRows';

interface TermRowsInputProps {
  rows: TermRowItem[];
  onChange: (rows: TermRowItem[]) => void;
  disabled: boolean;
}

const termInputId = (id: number) => `upload-term-${id}`;

const focusTerm = (id: number) => document.getElementById(termInputId(id))?.focus();

/**
 * 固有名詞の行入力（用語・読み仮名）。v3.7.1 Unit 005。
 * 用語欄のコロン分割は blur・貼り付けでだけ行う（キー入力・IME 変換中は分割しない）。
 * アップロード中は fieldset の disabled で入力・追加・削除をまとめて無効にする。
 */
export default function TermRowsInput({ rows, onChange, disabled }: TermRowsInputProps) {
  // 新しく描画される行へのフォーカス先。onChange のときだけ設定し、直後の描画で消費する。
  const pendingFocusId = useRef<number | null>(null);

  useEffect(() => {
    if (pendingFocusId.current === null) return;
    focusTerm(pendingFocusId.current);
    pendingFocusId.current = null;
  }, [rows]);

  const update = (next: TermRowItem[], focusId?: number) => {
    if (focusId !== undefined) pendingFocusId.current = focusId;
    onChange(next);
  };

  const updateRow = (index: number, row: TermRowItem) =>
    update(rows.map((r, i) => (i === index ? row : r)));

  const addRow = () => {
    const row = createTermRowItem();
    update([...rows, row], row.id);
  };

  // 最後の1行は消さず空の行に置き換える（常に1行以上）
  const removeRow = (index: number) => {
    if (rows.length === 1) {
      const row = createTermRowItem();
      update([row], row.id);
      return;
    }
    const next = rows.filter((_, i) => i !== index);
    update(next, next[Math.min(index, next.length - 1)].id);
  };

  const handleTermBlur = (index: number) => {
    const split = splitTermRow(rows[index]);
    if (split !== rows[index]) updateRow(index, split);
  };

  const handleTermPaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const lines = parseTermLines(text);
    if (lines.length === 0) return; // 空白・改行だけは既定の貼り付けに任せる
    e.preventDefault();
    const row = rows[index];

    if (lines.length === 1) {
      // 選択範囲を貼り付けた1行で置き換えてから分割する（末尾改行付きのコピーも1行として扱う）
      const line = text.split(LINE_BREAK).find((l) => l.trim()) ?? '';
      const start = e.currentTarget.selectionStart ?? row.term.length;
      const end = e.currentTarget.selectionEnd ?? row.term.length;
      updateRow(index, splitTermRow({ ...row, term: row.term.slice(0, start) + line + row.term.slice(end) }));
      return;
    }

    // 複数行: 空の行ならそこから埋め、空でなければその行の後ろに挿入する
    const items = lines.map((line) => createTermRowItem(line));
    const head = rows.slice(0, isBlankRow(row) ? index : index + 1);
    update([...head, ...items, ...rows.slice(index + 1)], items[items.length - 1].id);
  };

  // Enter は送信せず次の行へ進む（Textarea から input に変わり暗黙送信が起きるため）。
  // IME 確定の Enter は無視する（Safari は isComposing=false・keyCode 229 で送る）。
  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    const nextRow = rows[index + 1];
    if (nextRow) {
      focusTerm(nextRow.id);
    } else if (!isBlankRow(rows[index])) {
      addRow();
    }
  };

  return (
    <fieldset disabled={disabled} aria-describedby="upload-terms-hint" className="mb-6 min-w-0">
      <legend className="block text-sm font-medium mb-2">固有名詞（任意）</legend>
      <div className="space-y-2">
        {rows.map((row, index) => {
          const n = index + 1;
          return (
            <div key={row.id} className="flex items-center gap-2">
              <Input
                id={termInputId(row.id)}
                className="min-w-0 flex-1"
                aria-label={`用語 ${n}`}
                placeholder="用語"
                value={row.term}
                onChange={(e) => updateRow(index, { ...row, term: e.target.value })}
                onBlur={() => handleTermBlur(index)}
                onPaste={(e) => handleTermPaste(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
              <Input
                aria-label={`読み仮名 ${n}`}
                className="min-w-0 flex-1"
                placeholder="読み仮名（任意）"
                value={row.reading}
                onChange={(e) => updateRow(index, { ...row, reading: e.target.value })}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`${n} 行目を削除`}
                onClick={() => removeRow(index)}
                className="shrink-0"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          );
        })}
      </div>
      <Button variant="secondary" size="sm" onClick={addRow} className="mt-2">
        <Plus className="h-4 w-4" aria-hidden="true" />
        行を追加
      </Button>
      <p id="upload-terms-hint" className="mt-2 text-xs text-gray-500">
        このセッションのキャラ名・NPC・固有名詞を入れると認識精度が上がります。用語と読み仮名を1行ずつ入力してください（読み仮名は任意）。フルネームは姓と名の間に空白を入れます（例: 山田 太郎／やまだ たろう）。書き起こしで中点を残したい名前は中点で区切ります（例: ジョン・スミス）。数十語まで登録できます。
        <br />
        用語欄に「太郎：たろう」のように1行ずつ貼り付けると、読み仮名欄に分かれて入ります。例:
        <br />
        太郎：たろう
        <br />
        花子：はなこ
      </p>
    </fieldset>
  );
}
